import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {getUserSmsLimit} from './HostSettingsStore';

import {getBillingOrderInfoExternal, getOrganizationSettingForExternal, getStripePublicKey} from 'Api/Calendar';
import {getOrganizationSetting} from 'Api/ZCalendar';
import {alterFontListResp, normalizeFontList} from 'Utils/fontUtils';

export const fetchOrganizationSetting = createAsyncThunk(
  'organizationSetting/fetchOrganizationSetting',
  async (data, thunkAPI) => {
    const response = await getOrganizationSetting();
    if (!response.smsLimit) {
      // if sms is not forbidden at org level, fetch user level sms limit data
      thunkAPI.dispatch(getUserSmsLimit());
    }
    alterFontListResp(response);
    return response;
  }
);

export const fetchOrganizationSettingForExternal = createAsyncThunk(
  'organizationSetting/fetchOrganizationSettingForExternal',
  async (user) => {
    const response = await getOrganizationSettingForExternal(user);
    alterFontListResp(response);
    return response;
  }
);

export const fetchStripePublicKey = createAsyncThunk(
  'organizationSetting/fetchStripePublicKey',
  async () => {
    const response = await getStripePublicKey();
    return response;
  }
);

export const fetchBillingOrderInfoExternalThunk = createAsyncThunk(
  'organizationSetting/fetchBillingOrderInfoExternal',
  async (data, thunkAPI) => {
    const response = await getBillingOrderInfoExternal(data);
    return response;
  }
);

export const organizationSettingState = createSlice({
  name: 'organizationSettingStore',
  initialState: {
    isLoading: false,
    accountName: '',
    workflowAdminsOnly: false,
    createTeamAppointmentEnabled: true,
    vanityUrlEnabled: false,
    vanityUrl: '',
    availabilityOverride: true,
    shareAnythingQuestion: true,
    allowUserRequirePaymentEnabled: true,
    currencyAllowList: undefined,
    disabledConnectionServices: [],
    logoEnforceUse: false,
    emailNotificationEnabled: true,
    smsNotificationEnabled: true,
    isSmsLimited: false,
    customFontList: [],
    customFontLoaded: [],
    customFontLoadFailed: [],
    emailNoReplyNameEnabled: false,
    showPoweredByZoom: true, // default to true, can be overridden by org setting
    stripePublicKey: '',
    autoRefundPolicy: '',
    emailStyleEnforceUse: false,
    emailStyle: {},
  },
  reducers: {
    addCustomFontLoaded: (state, action) => {
      const loadedFontNames = (action.payload || []).map(({fontName}) => fontName);
      if (loadedFontNames.length) {
        state.customFontLoaded.push(...loadedFontNames);
      }
    },
    addCustomFontLoadFailed: (state, action) => {
      const loadFailedFontNames = action.payload || [];
      const failedSet = new Set(state.customFontLoadFailed);
      loadFailedFontNames.forEach((fontName) => {
        failedSet.add(fontName);
      });
      state.customFontLoadFailed = Array.from(failedSet);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrganizationSetting.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchOrganizationSetting.fulfilled, (state, action) => {
      state.isLoading = false;
      state.workflowAdminsOnly = action.payload.workflowAdminsOnly;
      state.createTeamAppointmentEnabled = action.payload.createTeamAppointmentEnabled;
      state.disabledConnectionServices = action.payload.disabledConnectionServices ?? [];
      state.vanityUrlEnabled = !!action.payload.vanityUrlEnabled;
      if (action.payload.vanityUrlEnabled) {
        state.vanityUrl = action.payload.vanityUrl;
      }
      state.isSmsLimited = !!action.payload.smsLimit;
      state.availabilityOverride = action.payload.availabilityOverride ?? true;
      state.shareAnythingQuestion = action.payload.shareAnythingQuestion ?? true;
      state.allowUserRequirePaymentEnabled = action.payload.allowUserRequirePaymentEnabled ?? true;
      state.currencyAllowList = action.payload.currencyAllowList;
      state.logoEnforceUse = !!action.payload.logoEnforceUse;
      // If the field is not present in the org profile, notifications are default enabled.
      state.smsNotificationEnabled = action.payload.smsNotificationEnabled !== false;
      state.emailNotificationEnabled = action.payload.emailNotificationEnabled !== false;
      state.customFontList = normalizeFontList(action.payload.fontList ?? []);
      state.emailNoReplyNameEnabled = !!action.payload.emailNoReplyNameEnabled;
      state.showPoweredByZoom = action.payload.showPoweredByZoom !== false; // default to true
      state.autoRefundPolicy = action.payload.autoRefundPolicy ?? 24; // default to 24 hours
      state.accountName = action.payload.accountName ?? '';
      state.emailStyleEnforceUse = !!action.payload.emailStyleEnforceUse;
      state.emailStyle = action.payload.emailStyle ?? {};
    });
    builder.addCase(fetchOrganizationSetting.rejected, (state) => {
      state.isLoading = false;
      state.workflowAdminsOnly = false;
      // set as true by default in case block crt users
      state.createTeamAppointmentEnabled = true;
    });
    builder.addCase(fetchStripePublicKey.fulfilled, (state, action) => {
      state.stripePublicKey = action.payload?.publicKey ?? '';
    });
  },
});

export const {
  addCustomFontLoadFailed,
  addCustomFontLoaded,
} = organizationSettingState.actions;

export default organizationSettingState.reducer;
